import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Loader2, ArrowRight, RefreshCw, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { CinematicVideo } from "@/components/CinematicVideo";

const SUGGEST = { fr: "Suggestions", en: "Suggestions", es: "Sugerencias", nl: "Suggesties" };

function Field({ label, children }) {
  return (
    <div>
      <p className="uppercase tracking-[0.22em] text-[11px] text-primary/75 mb-1.5">{label}</p>
      <p className="text-foreground/80 leading-relaxed">{children}</p>
    </div>
  );
}

function RootChips({ items }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <div className="flex flex-wrap justify-center gap-2.5">
      {items.map((r, i) => (
        <div key={i} className="rounded-2xl border border-primary/20 bg-primary/[0.04] px-4 py-2.5 text-center">
          <span className="font-display text-primary text-lg">{r.part}</span>
          <span className="block text-xs text-foreground/55 mt-0.5">{r.meaning}</span>
        </div>
      ))}
    </div>
  );
}

function WordList({ label, items }) {
  if (!Array.isArray(items) || !items.length) return null;
  return (
    <div>
      {label && <p className="uppercase tracking-[0.22em] text-[11px] text-primary/75 mb-2">{label}</p>}
      <ul className="space-y-2">
        {items.map((it, i) => {
          if (typeof it === "string") {
            return (
              <li key={i} className="flex items-center gap-2 text-foreground/80">
                <span className="h-1.5 w-1.5 rounded-full bg-primary/60 flex-shrink-0" /> {it}
              </li>
            );
          }
          return (
            <li key={i} className="flex gap-2.5 text-foreground/80">
              <span className="font-display text-primary">{it.word}</span>
              <span className="text-foreground/55">— {it.sense}</span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

function renderFields(data, tab, tr) {
  return (tab.fields || []).map((f, i) => {
    const val = data[f.key];
    if (val === undefined || val === null || (typeof val === "string" && !val)) return null;
    if (f.kind === "roots") return <RootChips key={i} items={val} />;
    if (f.kind === "list") return <WordList key={i} label={f.labelKey ? tr(`${tab.key}.${f.labelKey}`) : null} items={val} />;
    if (f.kind === "body") return <p key={i} className="text-foreground/75 leading-[1.8]">{val}</p>;
    return <Field key={i} label={tr(`${tab.key}.${f.labelKey}`)}>{val}</Field>;
  });
}

function SearchPanel({ ns, tab, lang }) {
  const { t } = useTranslation();
  const tr = (k) => t(`landing.${ns}.${k}`);
  const presets = t(`landing.${ns}.${tab.key}.presets`, { returnObjects: true }) || [];
  const [term, setTerm] = useState("");
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const run = async (value) => {
    const q = (value ?? term).trim();
    if (!q) return;
    setTerm(q);
    setLoading(true);
    try {
      const { data: res } = await api.get(tab.endpoint, { params: { [tab.param]: q, lang }, timeout: 60000 });
      setData(res);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr(`${tab.key}.error`));
      setData(null);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <p className="text-foreground/60 leading-[1.8] text-center max-w-xl mx-auto mb-6">{tr(`${tab.key}.subtitle`)}</p>
      <form onSubmit={(e) => { e.preventDefault(); run(); }} className="flex flex-col sm:flex-row gap-2.5 max-w-xl mx-auto mb-4">
        <input
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder={tr(`${tab.key}.placeholder`)}
          data-testid={`${ns}-${tab.key}-input`}
          className="flex-1 rounded-full bg-white/[0.03] border border-white/12 px-5 py-3 text-foreground placeholder:text-foreground/35 focus:outline-none focus:border-primary/45 transition-colors"
        />
        <button type="submit" disabled={loading || !term.trim()} data-testid={`${ns}-${tab.key}-submit`}
          className="inline-flex items-center justify-center gap-2 rounded-full bg-primary/15 border border-primary/40 text-primary px-6 py-3 text-sm font-medium hover:bg-primary/25 transition-colors disabled:opacity-50">
          {loading ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />} {tr(`${tab.key}.go`)}
        </button>
      </form>
      {presets.length > 0 && (
        <div className="mt-2 mb-2 max-w-xl mx-auto rounded-2xl border border-white/10 bg-white/[0.02] p-4">
          <p className="text-[0.7rem] uppercase tracking-[0.22em] text-primary/70 mb-3 flex items-center justify-center gap-1.5">
            <Sparkles size={12} /> {SUGGEST[lang] || SUGGEST.fr}
          </p>
          <div className="flex flex-wrap justify-center gap-2">
            {presets.map((p, i) => (
              <button key={i} onClick={() => run(p)} disabled={loading} data-testid={`${ns}-${tab.key}-preset-${i}`}
                className="text-xs text-foreground/55 border border-white/10 rounded-full px-3 py-1.5 hover:text-primary hover:border-primary/30 transition-colors disabled:opacity-50">
                {p}
              </button>
            ))}
          </div>
        </div>
      )}
      {loading && (
        <p className="text-center font-display italic text-foreground/40 flex items-center justify-center gap-2 py-7">
          <Loader2 size={16} className="animate-spin" /> {tr(`${tab.key}.loading`)}
        </p>
      )}
      <AnimatePresence mode="wait">
        {!loading && data && (
          <motion.div key={term} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.35 }} className="mt-6 space-y-5 text-left max-w-xl mx-auto" data-testid={`${ns}-${tab.key}-result`}>
            <div className="text-center">
              <p className="font-display text-gradient-orange text-2xl sm:text-3xl">{data[tab.titleKey]}</p>
              {tab.metaKey && data[tab.metaKey] && <p className="text-xs uppercase tracking-[0.2em] text-primary/70 mt-1">{data[tab.metaKey]}</p>}
            </div>
            {renderFields(data, tab, tr)}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function WordPanel({ ns, tab, lang }) {
  const { t } = useTranslation();
  const tr = (k) => t(`landing.${ns}.${k}`);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = async (refresh = false) => {
    setLoading(true);
    try {
      const { data: res } = await api.get(tab.endpoint, { params: { refresh, lang }, timeout: 60000 });
      setData(res);
    } catch (e) {
      if (refresh) toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr(`${tab.key}.error`));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(false); }, [lang]); // eslint-disable-line

  return (
    <div className="text-center" data-testid={`${ns}-word`}>
      {loading && !data ? (
        <p className="font-display italic text-foreground/40 flex items-center justify-center gap-2 py-7">
          <Loader2 size={16} className="animate-spin" /> {tr(`${tab.key}.loading`)}
        </p>
      ) : data ? (
        <AnimatePresence mode="wait">
          <motion.div key={data[tab.titleKey]} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.4 }} className="max-w-xl mx-auto space-y-5 text-left">
            <div className="text-center">
              <p className="font-display text-gradient-orange text-3xl sm:text-4xl">{data[tab.titleKey]}</p>
              {tab.metaKey && data[tab.metaKey] && <p className="text-xs uppercase tracking-[0.2em] text-primary/70 mt-1">{data[tab.metaKey]}</p>}
            </div>
            {renderFields(data, tab, tr)}
          </motion.div>
        </AnimatePresence>
      ) : (
        <p className="font-display italic text-foreground/40 py-7">{tr(`${tab.key}.error`)}</p>
      )}
      <div className="mt-7">
        <button onClick={() => load(true)} disabled={loading} data-testid={`${ns}-word-refresh`}
          className="inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors disabled:opacity-50">
          <RefreshCw size={13} className={loading ? "animate-spin" : ""} /> {tr(`${tab.key}.refresh`)}
        </button>
      </div>
    </div>
  );
}

function QuizPanel({ ns, tab, lang }) {
  const { t } = useTranslation();
  const tr = (k) => t(`landing.${ns}.${k}`);
  const [round, setRound] = useState(null);
  const [loading, setLoading] = useState(true);
  const [picked, setPicked] = useState(null);

  const load = async () => {
    setLoading(true); setPicked(null); setRound(null);
    try {
      const { data } = await api.get(tab.endpoint, { params: { lang }, timeout: 60000 });
      setRound(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || tr(`${tab.key}.error`));
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [lang]); // eslint-disable-line
  const answered = picked !== null;
  const prompt = round?.definition || round?.clue;

  return (
    <div className="text-center" data-testid={`${ns}-quiz`}>
      <p className="text-foreground/60 leading-[1.8] max-w-xl mx-auto mb-6">{tr(`${tab.key}.subtitle`)}</p>
      {loading ? (
        <p className="font-display italic text-foreground/40 flex items-center justify-center gap-2 py-7">
          <Loader2 size={16} className="animate-spin" /> {tr(`${tab.key}.loading`)}
        </p>
      ) : round ? (
        <div className="max-w-xl mx-auto">
          {Array.isArray(round.roots) && round.roots.length > 0 && (
            <div className="mb-4"><RootChips items={round.roots} /></div>
          )}
          {prompt && <p className="font-display italic text-lg sm:text-xl text-foreground/85 mb-2">{prompt}</p>}
          <p className="text-sm text-foreground/50 mb-6">{tr(`${tab.key}.question`)}</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
            {round.choices.map((c, i) => {
              const isAnswer = c === round.answer;
              const isPicked = c === picked;
              let cls = "text-foreground/70 border-white/12 hover:border-primary/30 hover:text-primary";
              if (answered && isAnswer) cls = "border-emerald-400/50 bg-emerald-400/10 text-emerald-300";
              else if (answered && isPicked) cls = "border-red-400/50 bg-red-400/10 text-red-300";
              else if (answered) cls = "text-foreground/40 border-white/8";
              return (
                <button key={i} disabled={answered} onClick={() => setPicked(c)} data-testid={`${ns}-quiz-choice-${i}`}
                  className={`rounded-full border px-5 py-3 text-sm font-medium transition-colors disabled:cursor-default ${cls}`}>{c}</button>
              );
            })}
          </div>
          {answered && (
            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }}
              className={`mt-6 font-display italic ${picked === round.answer ? "text-emerald-300" : "text-red-300"}`} data-testid={`${ns}-quiz-verdict`}>
              {picked === round.answer ? tr(`${tab.key}.correct`) : tr(`${tab.key}.wrong`)}
            </motion.p>
          )}
          <div className="mt-6">
            <button onClick={load} data-testid={`${ns}-quiz-new`}
              className="inline-flex items-center gap-1.5 text-xs text-foreground/45 hover:text-primary transition-colors">
              <RefreshCw size={13} /> {answered ? tr(`${tab.key}.again`) : tr(`${tab.key}.newRound`)}
            </button>
          </div>
        </div>
      ) : (
        <p className="font-display italic text-foreground/40 py-7">{tr(`${tab.key}.error`)}</p>
      )}
    </div>
  );
}

export function GenericLexique({ ns, tabs, icons, embedded = false, video }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.language || "fr").slice(0, 2);
  const [active, setActive] = useState(0);
  const tab = tabs[active];

  return (
    <section className="relative overflow-hidden" data-testid={`${ns}-lexique-section`}>
      <div className={`max-w-4xl mx-auto px-6 ${embedded ? "pt-2 pb-8" : "py-16 sm:py-20"}`}>
        <div className="text-center mb-8">
          <h2 className="font-display text-gradient-orange text-4xl sm:text-5xl leading-[1.08] tracking-tight mb-4" data-testid={`${ns}-lexique-title`}>
            {t(`landing.${ns}.title`)}
          </h2>
          <p className="text-foreground/55 italic max-w-md mx-auto">{t(`landing.${ns}.subtitle`)}</p>
        </div>

        {video && (
          <div className="mb-10 max-w-3xl mx-auto">
            <CinematicVideo fade noSound src={video} testId={`${ns}-cinematic-video`} caption={t(`landing.${ns}.title`)} />
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2.5 sm:gap-3 mb-8" role="tablist">
          {tabs.map((tb, i) => {
            const Icon = icons[i];
            const on = active === i;
            return (
              <button key={tb.key} onClick={() => setActive(i)} data-testid={`${ns}-tab-${tb.key}`} role="tab" aria-selected={on}
                className={`group card-sheen relative flex flex-col items-center justify-center gap-2.5 h-24 sm:h-28 rounded-2xl border px-2.5 text-center overflow-hidden transition-colors duration-300 ${
                  on
                    ? "border-primary/50 bg-primary/[0.08] text-primary shadow-[0_12px_44px_-14px_hsl(28_96%_56%_/_0.6)]"
                    : "border-white/10 bg-white/[0.02] text-foreground/60 hover:border-primary/30 hover:text-primary/90"
                }`}>
                {on && (
                  <span aria-hidden className="pointer-events-none absolute inset-0" style={{ background: "radial-gradient(circle at 50% 28%, hsl(28 96% 56% / 0.18), transparent 68%)" }} />
                )}
                <span className={`relative z-10 flex items-center justify-center w-10 h-10 rounded-full border transition-colors duration-300 ${on ? "border-primary/50 bg-primary/15" : "border-white/10 bg-white/[0.03] group-hover:border-primary/30"}`}>
                  <Icon size={18} strokeWidth={1.7} className={on ? "pulse-star" : ""} />
                </span>
                <span className="relative z-10 text-[11px] sm:text-xs font-medium leading-tight line-clamp-2">{t(`landing.${ns}.${tb.labelKey}`)}</span>
              </button>
            );
          })}
        </div>

        <AnimatePresence mode="wait">
          <motion.div key={active} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }} className="glass stats-wow rounded-2xl border border-primary/15 p-6 sm:p-8" data-testid={`${ns}-panel`}>
            {tab.type === "search" && <SearchPanel ns={ns} tab={tab} lang={lang} />}
            {tab.type === "quiz" && <QuizPanel ns={ns} tab={tab} lang={lang} />}
            {tab.type === "word" && <WordPanel ns={ns} tab={tab} lang={lang} />}
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
